import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@apps-common/ui-components/DialogTitle.jsx';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PropTypes from 'prop-types';
import React, {Fragment, useState} from 'react';
import {ASSETS_URL} from '../../../config.js';
import {Link} from 'react-router-dom';
import {connect} from 'react-redux';
import {deleteStory} from '../../../api/stories.js';
import {styled} from '@mui/material/styles';
import {theme} from '../../../styles.js';
import {useMutation, useQueryClient} from '@tanstack/react-query';

const MoreButton = styled(IconButton)({
  margin: theme.spacing(-0.5),
  marginLeft: 0,
});

const StoryMenu = props => {
  const queryClient = useQueryClient();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const onMoreClick = event => {
    setMenuAnchor(event.target);
    setMenuOpen(true);
  };

  const onDownloadClick = () => analytics.track('Download story', props.story);

  const onDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const {mutate: onDeleteStory, isLoading: isDeleting} = useMutation({
    mutationFn: storyId => {
      return deleteStory(storyId);
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({
        queryKey: ['stories', {own: true}],
      });
    },
    onSuccess: () => {
      closeDeleteDialog();
      closeMenu();
    },
  });

  return (
    <Fragment>
      <MoreButton onClick={onMoreClick} data-cy="story-actions">
        <MoreHorizIcon />
      </MoreButton>{' '}
      <Menu
        open={menuOpen}
        onClose={closeMenu}
        anchorEl={menuAnchor}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
      >
        <MenuItem
          component={Link}
          to={`/${props.story.id}`}
          data-cy="story-action-view"
        >
          View
        </MenuItem>
        {props.story.format === 'mp4' && props.story.status === 'Complete' && (
          <MenuItem
            download
            component="a"
            href={`${ASSETS_URL}/${props.story.id}/movie.mp4`}
            onClick={onDownloadClick}
          >
            Download
          </MenuItem>
        )}
        <MenuItem onClick={onDeleteClick} data-cy="story-action-delete">
          Delete
        </MenuItem>
      </Menu>
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle
          data-cy="popup-title"
          onClose={closeDeleteDialog}
          title="Are you sure?"
        />

        <DialogContent>
          <DialogContentText data-cy="popup-description">
            Your story, &quot;
            {props.story.title}
            &quot; will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            onClick={closeDeleteDialog}
            data-cy="do-not-delete-button"
          >
            No
          </Button>
          <Button
            autoFocus
            color="primary"
            variant="contained"
            disabled={isDeleting}
            onClick={() => {
              onDeleteStory(props.story.id);
            }}
            data-cy="delete-button"
          >
            {isDeleting ? 'Deleting...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

StoryMenu.propTypes = {
  story: PropTypes.object.isRequired,
};

export default connect()(StoryMenu);
